import type jsPDF from 'jspdf';
import { toPdfSafe } from './pdfSafe';

/**
 * Moteur de rendu PDF « prose » (jsPDF) — titres `##`/`###`, puces avec
 * indentation suspendue, gras inline `**…**`, wrapping mot-à-mot, justification
 * et pagination automatique. Texte SÉLECTIONNABLE (décision 2026-06-16), jamais
 * une capture image.
 *
 * WHY ce moteur vit ici et non dans `apps/legalworkspace/utils/analysisExport` :
 * le backend rend désormais le PDF d'une décision via le flux
 * `save_jurisprudence` → `saveDecisionAsCaseDocument`
 * avec EXACTEMENT la même mise en page que le bouton « Télécharger » du viewer.
 * Un package ne pouvant pas importer depuis une app, le moteur a été remonté ici et
 * `proseExport.ts` (legalworkspace) le consomme désormais depuis ce package — il
 * reste l'unique implémentation, il n'y a pas de copie.
 *
 * jsPDF n'embarque que les polices standard : la prose est rendue en `times`
 * (serif intégré), `Garamond` n'existant que côté Word.
 */
const PDF_FONT = 'times';


export const PROSE_MARGIN_MM = 16;
const MARGIN_MM = PROSE_MARGIN_MM;
/** Segment de texte enrichi : portion de ligne avec ou sans gras. */
export interface RichSegment {
  text: string;
  bold: boolean;
}

/** Découpe une ligne markdown inline en segments gras/non-gras (`**…**`). */
function parseBoldSegments(line: string): RichSegment[] {
  const segments: RichSegment[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) segments.push({ text: line.slice(last, m.index), bold: false });
    segments.push({ text: m[1] ?? "", bold: true });
    last = m.index + m[0].length;
  }
  if (last < line.length) segments.push({ text: line.slice(last), bold: false });
  return segments.length > 0 ? segments : [{ text: line, bold: false }];
}

/** Curseur de rendu PDF (position verticale + bornes de page). */
export interface Cursor {
  pdf: jsPDF;
  y: number;
  readonly maxY: number;
  readonly leftX: number;
  readonly rightX: number;
}

/** Crée un curseur de rendu prose pour un PDF existant. */
export function makeProseCursor(pdf: jsPDF, startY: number): Cursor {
  return {
    pdf,
    y: startY,
    maxY: pdf.internal.pageSize.getHeight() - MARGIN_MM,
    leftX: MARGIN_MM,
    rightX: pdf.internal.pageSize.getWidth() - MARGIN_MM,
  };
}

function newPageIfNeeded(c: Cursor, lineHeight: number): void {
  if (c.y + lineHeight > c.maxY) {
    c.pdf.addPage();
    c.y = MARGIN_MM;
  }
}

/** Tailles de police (pt) et rythme vertical du moteur prose PDF. */
const BODY_PT = 10.5;
const H2_PT = 13.5;
const H3_PT = 11.5;
const LINE_RATIO = 1.5; // interligne
const CHARCOAL: [number, number, number] = [17, 24, 39];
const MUTED: [number, number, number] = [75, 85, 99]; // gray-600
const BULLET_INDENT = 3.5; // mm — décalage de la puce
const BULLET_TEXT_GAP = 3.2; // mm — espace puce → texte (indentation suspendue)

export interface LineOpts {
  firstIndent?: number;
  contIndent?: number;
  color?: [number, number, number];
  gapBefore?: number;
  /** Texte justifié (aligné aux deux marges) sauf la dernière ligne du paragraphe. */
  justify?: boolean;
}

/** Jeton de rendu : mot ou espace, avec sa police et sa largeur mesurée. */
interface Token {
  text: string;
  bold: boolean;
  isSpace: boolean;
  width: number;
}

/**
 * Rend des segments enrichis : tokenisation, empaquetage en lignes visuelles,
 * **indentation suspendue** (`contIndent`), pagination, et **justification**
 * (`opts.justify`) — la slack est répartie sur les espaces, sauf la dernière ligne
 * du paragraphe (qui reste alignée à gauche, convention typographique). Tout texte
 * est assaini (`toPdfSafe`).
 */
export function renderRichLine(c: Cursor, segments: RichSegment[], fontSizePt: number, opts: LineOpts = {}): void {
  const lineHeight = fontSizePt * 0.3528 * LINE_RATIO; // pt → mm × interligne
  if (opts.gapBefore) c.y += opts.gapBefore;
  const firstX = c.leftX + (opts.firstIndent ?? 0);
  const contX = c.leftX + (opts.contIndent ?? opts.firstIndent ?? 0);
  c.pdf.setFontSize(fontSizePt);

  // 1) Tokeniser en mots/espaces, largeur mesurée selon la police du segment.
  const toks: Token[] = [];
  for (const seg of segments) {
    c.pdf.setFont(PDF_FONT, seg.bold ? "bold" : "normal");
    for (const part of toPdfSafe(seg.text).split(/(\s+)/)) {
      if (!part) continue;
      const isSpace = /^\s+$/.test(part);
      const text = isSpace ? " " : part;
      toks.push({ text, bold: seg.bold, isSpace, width: c.pdf.getTextWidth(text) });
    }
  }

  // 2) Empaqueter (greedy) en lignes visuelles ; pas d'espace en tête de ligne.
  const lines: Token[][] = [];
  let cur: Token[] = [];
  let curW = 0;
  let avail = c.rightX - firstX;
  const dropTrailingSpaces = () => {
    while (cur.length && cur[cur.length - 1]?.isSpace) {
      curW -= cur[cur.length - 1]?.width ?? 0;
      cur.pop();
    }
  };
  for (const t of toks) {
    if (t.isSpace) {
      if (cur.length === 0) continue;
      cur.push(t);
      curW += t.width;
    } else {
      if (curW + t.width > avail && cur.length > 0) {
        dropTrailingSpaces();
        lines.push(cur);
        cur = [];
        curW = 0;
        avail = c.rightX - contX;
      }
      cur.push(t);
      curW += t.width;
    }
  }
  if (cur.length) {
    dropTrailingSpaces();
    if (cur.length) lines.push(cur);
  }

  // 3) Rendre chaque ligne (justifiée sauf la dernière du paragraphe).
  c.pdf.setTextColor(...(opts.color ?? CHARCOAL));
  lines.forEach((line, i) => {
    newPageIfNeeded(c, lineHeight);
    const startX = i === 0 ? firstX : contX;
    const isLast = i === lines.length - 1;
    const natural = line.reduce((s, t) => s + t.width, 0);
    const nbSpaces = line.reduce((n, t) => n + (t.isSpace ? 1 : 0), 0);
    const availW = c.rightX - startX;
    const extra =
      opts.justify && !isLast && nbSpaces > 0 && natural < availW
        ? (availW - natural) / nbSpaces
        : 0;
    let x = startX;
    for (const t of line) {
      if (t.isSpace) {
        x += t.width + extra;
      } else {
        c.pdf.setFont(PDF_FONT, t.bold ? "bold" : "normal");
        c.pdf.text(t.text, x, c.y);
        x += t.width;
      }
    }
    c.y += lineHeight;
  });
}

/** Dessine une puce vectorielle pleine, alignée à la ligne de base courante. */
function drawBullet(c: Cursor, fontSizePt: number): void {
  const cy = c.y - fontSizePt * 0.3528 * 0.32;
  c.pdf.setFillColor(...CHARCOAL);
  c.pdf.circle(c.leftX + BULLET_INDENT + 0.5, cy, 0.55, "F");
}

/** Sépare une éventuelle étiquette « Prochaine étape : » pour la mettre en gras. */
function continuationSegments(line: string): RichSegment[] {
  const m = line.match(/^(Prochaine étape\s*:)\s*([\s\S]*)$/);
  if (m) return [{ text: m[1] + " ", bold: true }, { text: m[2] ?? "", bold: false }];
  return parseBoldSegments(line);
}

/**
 * Rend du markdown dans un PDF existant via le curseur fourni (titres `##`/`###`,
 * puces avec indentation suspendue, lignes de continuation indentées et atténuées,
 * gras inline, wrapping + pagination). Réutilisé par l'export unitaire ET le
 * compositeur Rapport complet. Ne fait ni titre ni sauvegarde.
 */
export function renderProseBody(c: Cursor, markdown: string): void {
  for (const raw of markdown.split("\n")) {
    const indentSpaces = raw.length - raw.trimStart().length; // détecté AVANT trim
    const line = raw.trim();
    if (line.length === 0) {
      c.y += 5; // respiration entre blocs (paragraphes plus aérés)
      continue;
    }
    if (line.startsWith("## ")) {
      renderRichLine(c, [{ text: line.slice(3).trim(), bold: true }], H2_PT, { gapBefore: 5 });
      c.y += 1.5;
    } else if (line.startsWith("### ")) {
      renderRichLine(c, [{ text: line.slice(4).trim(), bold: true }], H3_PT, {
        gapBefore: 3.5,
      });
      c.y += 0.5;
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      // Espace avant chaque puce (pas la continuation, qui doit coller à sa puce).
      c.y += 4;
      // Garantir que la puce et sa 1re ligne tiennent sur la même page.
      newPageIfNeeded(c, BODY_PT * 0.3528 * LINE_RATIO);
      drawBullet(c, BODY_PT);
      renderRichLine(c, parseBoldSegments(line.slice(2).trim()), BODY_PT, {
        firstIndent: BULLET_INDENT + BULLET_TEXT_GAP,
        contIndent: BULLET_INDENT + BULLET_TEXT_GAP,
        justify: true,
      });
    } else if (indentSpaces >= 2) {
      // Ligne de continuation (ex. « Prochaine étape : … ») : nichée sous la puce,
      // étiquette en gras (continuationSegments) + texte atténué, léger filet d'air.
      renderRichLine(c, continuationSegments(line), BODY_PT, {
        firstIndent: BULLET_INDENT + BULLET_TEXT_GAP,
        contIndent: BULLET_INDENT + BULLET_TEXT_GAP,
        color: MUTED,
        justify: true,
        gapBefore: 1.5,
      });
    } else {
      renderRichLine(c, parseBoldSegments(line), BODY_PT, { justify: true });
    }
  }
}
